import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMenu, FiX } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';

const HeaderContainer = styled.header`
  background: white;
  padding: 1rem 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const SewaLogoImage = styled.img`
  height: 60px;
  width: auto;
  margin-right: 8px;
  /* No filter - use original logo colors */
`;

const SewaLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 8px;
`;

const SewaLogoFallback = styled.div`
  background: linear-gradient(135deg, #4C1D95 0%, #6366F1 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.1;
  min-width: 50px;
`;

const BayAreaText = styled.div`
  font-size: 0.4rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--primary-blue);
  margin-top: 2px;
`;

const LogoText = styled.h1`
  color: var(--primary-blue);
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
`;

const BurritoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(45deg, #D4A574, #F4D03F);
  border-radius: 8px 4px 4px 8px;
  position: relative;
  border: 2px solid #B8860B;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 3px;
    background: #228B22;
    top: 8px;
    border-radius: 2px;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 18px;
    height: 3px;
    background: #DC143C;
    bottom: 8px;
    border-radius: 2px;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    background: var(--primary-blue);
    padding: 1rem;
    gap: 1rem;
  }
`;

const NavLink = styled.a`
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: var(--primary-orange);
  }

  @media (max-width: 768px) {
    color: white;
    
    &:hover {
      color: var(--warm-gold);
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--primary-blue);
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first, then scroll
    if (location.pathname !== '/') {
      navigate('/');
      // Small delay to let the page load before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const navigateToHome = () => {
    // Always navigate to home page and scroll to top
    navigate('/');
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <Nav>
        <Logo onClick={navigateToHome}>
          <SewaLogoContainer>
            <SewaLogoImage 
              src="/sewa-logo.png" 
              alt="Sewa USA"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <SewaLogoFallback style={{display: 'none'}}>
              SEWA<br/>USA
            </SewaLogoFallback>
            <BayAreaText>Bay Area</BayAreaText>
          </SewaLogoContainer>
          <BurritoIcon></BurritoIcon>
          <LogoText>Ahaar Sewa</LogoText>
        </Logo>
        
        <NavLinks isOpen={isMenuOpen}>
          <li><NavLink onClick={navigateToHome}>Home</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('what-is-burrito-sewa')}>What is Ahaar Sewa</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('why-sponsor')}>Why Sponsor</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('life-events')}>Life Events</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('past-events')}>Past Events</NavLink></li>
          <li><NavLink onClick={() => scrollToSection('contact')}>Contact</NavLink></li>
        </NavLinks>

        <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <FiX /> : <FiMenu />}
        </MenuButton>
      </Nav>
    </HeaderContainer>
  );
};

export default Header; 